/**
 * Enhanced Product Recommendations for Jewelry Store
 * Add this to a new JavaScript file in your theme assets
 */

class JewelryRecommendations {
  constructor(options = {}) {
    this.container = options.container || document.querySelector('.product-recommendations');
    this.productId = options.productId || this.getProductIdFromPage();
    this.limit = options.limit || 4;
    this.sectionId = options.sectionId || 'product-recommendations';
    this.recommendationType = options.recommendationType || 'related'; // 'related', 'complementary', 'recently-viewed'
    this.recentlyViewedKey = 'jewelry_recently_viewed';
    
    // Initialize if we have a container and product ID
    if (this.container && this.productId) {
      this.init();
    }
  }
  
  init() {
    // Track recently viewed product
    this.trackRecentlyViewed();
    
    // Get recommendations based on type
    switch (this.recommendationType) {
      case 'related':
        this.getRelatedProducts();
        break;
      case 'complementary':
        this.getComplementaryProducts();
        break;
      case 'recently-viewed':
        this.getRecentlyViewedProducts();
        break;
      default:
        this.getRelatedProducts();
    }
  }
  
  getProductIdFromPage() {
    // Get product ID from the page meta tag
    const productMeta = document.querySelector('meta[property="og:product_id"]');
    if (productMeta) {
      return productMeta.getAttribute('content');
    }
    
    // Fallback: try to get from JSON data
    const productJsonElement = document.getElementById('ProductJson-product-template');
    if (productJsonElement) {
      try {
        const productData = JSON.parse(productJsonElement.textContent);
        return productData.id;
      } catch (e) {
        console.error('Error parsing product JSON', e);
      }
    }
    
    return null;
  }
  
  trackRecentlyViewed() {
    if (!this.productId) return;
    
    // Get current product data
    const productTitle = document.querySelector('.product__title')?.textContent.trim();
    const productImage = document.querySelector('.product__media img')?.src;
    const productUrl = window.location.pathname;
    const productPrice = document.querySelector('.price__regular .price-item')?.textContent.trim();
    
    const currentProduct = {
      id: this.productId,
      title: productTitle,
      image: productImage,
      url: productUrl,
      price: productPrice,
      timestamp: new Date().getTime()
    };
    
    // Get existing recently viewed products from localStorage
    let recentlyViewed = [];
    try {
      const saved = localStorage.getItem(this.recentlyViewedKey);
      if (saved) {
        recentlyViewed = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading recently viewed products', e);
    }
    
    // Remove current product if it exists in the list
    recentlyViewed = recentlyViewed.filter(product => product.id != this.productId);
    
    // Add current product to the beginning
    recentlyViewed.unshift(currentProduct);
    
    // Limit the list to 20
    if (recentlyViewed.length > 20) {
      recentlyViewed = recentlyViewed.slice(0, 20);
    }
    
    // Save back to localStorage
    try {
      localStorage.setItem(this.recentlyViewedKey, JSON.stringify(recentlyViewed));
    } catch (e) {
      console.error('Error saving recently viewed products', e);
    }
  }
  
  getRelatedProducts() {
    // Use Shopify's product recommendations API
    const url = `/recommendations/products?section_id=${this.sectionId}&product_id=${this.productId}&limit=${this.limit}`;
    
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        
        const recommendations = html.querySelector('.product-recommendations');
        
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.container.innerHTML = recommendations.innerHTML;
          this.initProductCards();
          this.enhanceRecommendationDisplay();
        } else {
          // Fallback to collection products if no recommendations
          this.getCollectionProducts();
        }
      })
      .catch(e => {
        console.error('Error loading recommendations', e);
        this.getCollectionProducts();
      });
  }
  
  getComplementaryProducts() {
    // First, get the current product data to determine complementary items
    this.getProductData(this.productId)
      .then(productData => {
        if (!productData) {
          this.getRelatedProducts(); // Fallback
          return;
        }
        
        // Get product type, collection, tags, etc.
        const productType = productData.product_type;
        const productTags = productData.tags;
        
        // Get complementary items based on rules
        // e.g., if current product is a necklace, suggest matching earrings
        let complementaryType = '';
        
        if (productType.toLowerCase().includes('necklace') || productType.toLowerCase().includes('pendant')) {
          complementaryType = 'Earrings';
        } else if (productType.toLowerCase().includes('earring')) {
          complementaryType = 'Necklaces';
        } else if (productType.toLowerCase().includes('ring')) {
          complementaryType = 'Bracelets';
        } else if (productType.toLowerCase().includes('bracelet')) {
          complementaryType = 'Rings';
        }
        
        // Get products from complementary type
        if (complementaryType) {
          this.getProductsByType(complementaryType);
        } else {
          // Fallback to related products
          this.getRelatedProducts();
        }
      })
      .catch(e => {
        console.error('Error getting complementary products', e);
        this.getRelatedProducts(); // Fallback
      });
  }
  
  getProductsByType(productType, limit = this.limit) {
    // Construct a query to fetch products by type
    const query = `
      query {
        products(first: ${limit}, query: "product_type:'${productType}'") {
          edges {
            node {
              id
              title
              handle
              featuredImage {
                url
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;
    
    // Call Shopify's GraphQL API
    // Note: In a production environment, this would require an app proxy or AJAX API endpoint
    // For this example, we'll use a simplified approach with a fallback
    
    // Fallback to collection products
    this.getCollectionProducts();
  }
  
  getRecentlyViewedProducts() {
    try {
      const saved = localStorage.getItem(this.recentlyViewedKey);
      if (saved) {
        const recentlyViewed = JSON.parse(saved);
        
        // Remove current product
        const filtered = recentlyViewed.filter(product => product.id != this.productId);
        
        if (filtered.length) {
          this.renderRecentlyViewed(filtered.slice(0, this.limit));
        } else {
          this.getRelatedProducts(); // Fallback
        }
      } else {
        this.getRelatedProducts(); // Fallback
      }
    } catch (e) {
      console.error('Error loading recently viewed products', e);
      this.getRelatedProducts(); // Fallback
    }
  }
  
  renderRecentlyViewed(products) {
    // Build HTML for recently viewed products
    let html = `
      <div class="recently-viewed">
        <h2 class="recently-viewed__heading">Recently Viewed</h2>
        <ul class="recently-viewed__list grid grid--2-col grid--4-col-desktop">
    `;
    
    products.forEach(product => {
      html += `
        <li class="grid__item">
          <div class="card">
            <div class="card__inner">
              <a href="${product.url}" class="full-unstyled-link">
                <div class="card__media">
                  <div class="media media--transparent media--square">
                    <img src="${product.image}" alt="${product.title}" loading="lazy">
                  </div>
                </div>
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading">${product.title}</h3>
                    <div class="card__price">
                      <span class="price">${product.price}</span>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </li>
      `;
    });
    
    html += `</ul></div>`;
    
    // Insert into container
    this.container.innerHTML = html;
    
    // Initialize product card animations
    this.initProductCards();
  }
  
  getCollectionProducts() {
    // Get current collection, if any
    const collectionHandle = this.getCollectionHandleFromPage();
    
    if (!collectionHandle) {
      this.container.innerHTML = ''; // No recommendations available
      return;
    }
    
    // Get collection products
    const url = `/collections/${collectionHandle}?section_id=collection-template`;
    
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        
        // Find product cards
        const productCards = html.querySelectorAll('.card-wrapper');
        
        if (productCards.length) {
          // Create a container for collection products
          let collectionHtml = `
            <div class="collection-recommendations">
              <h2>More From This Collection</h2>
              <ul class="grid grid--2-col grid--4-col-desktop">
          `;
          
          // Add random products from collection (excluding current product)
          let productsToShow = Array.from(productCards)
            .filter(card => {
              const productId = card.dataset.productId;
              return productId && productId != this.productId;
            })
            .sort(() => 0.5 - Math.random()) // Shuffle
            .slice(0, this.limit);
          
          productsToShow.forEach(card => {
            collectionHtml += `<li class="grid__item">${card.outerHTML}</li>`;
          });
          
          collectionHtml += `</ul></div>`;
          
          this.container.innerHTML = collectionHtml;
          this.initProductCards();
        }
      })
      .catch(e => {
        console.error('Error loading collection products', e);
        this.container.innerHTML = ''; // No recommendations available
      });
  }
  
  getCollectionHandleFromPage() {
    // Try to get collection handle from breadcrumbs or URL
    const breadcrumbs = document.querySelector('.breadcrumbs');
    if (breadcrumbs) {
      const collectionLink = breadcrumbs.querySelector('a[href*="/collections/"]');
      if (collectionLink) {
        const href = collectionLink.getAttribute('href');
        const match = href.match(/\/collections\/([^/]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }
    }
    
    // Try from URL
    const urlMatch = window.location.pathname.match(/\/collections\/([^/]+)/);
    if (urlMatch && urlMatch[1]) {
      return urlMatch[1];
    }
    
    return null;
  }
  
  getProductData(productId) {
    return new Promise((resolve, reject) => {
      // Try to get product data from the page first
      const productJsonElement = document.getElementById('ProductJson-product-template');
      if (productJsonElement) {
        try {
          const productData = JSON.parse(productJsonElement.textContent);
          resolve(productData);
          return;
        } catch (e) {
          console.error('Error parsing product JSON', e);
        }
      }
      
      // Otherwise fetch it
      fetch(`/products/${productId}.js`)
        .then(response => response.json())
        .then(data => resolve(data))
        .catch(error => {
          console.error('Error fetching product data:', error);
          resolve(null);
        });
    });
  }
  
  initProductCards() {
    // Re-initialize any product card animations or hover effects
    const productCards = this.container.querySelectorAll('.card-wrapper');
    
    productCards.forEach(card => {
      // Add animations similar to the product listing pages
      card.classList.add('animate-fadeInUp');
      
      // Stagger the animation
      const delay = 100 + Math.random() * 300;
      card.style.animationDelay = `${delay}ms`;
    });
  }
  
  enhanceRecommendationDisplay() {
    // Add visual enhancements to recommendations section
    
    // Add heading if needed
    const heading = this.container.querySelector('h2');
    if (!heading) {
      const headingElement = document.createElement('h2');
      headingElement.classList.add('recommendation-heading');
      headingElement.textContent = 'You May Also Like';
      this.container.insertBefore(headingElement, this.container.firstChild);
    }
    
    // Add "why we recommend this" information to each product
    const productCards = this.container.querySelectorAll('.card-wrapper');
    
    productCards.forEach(card => {
      // Add a reason tag to show why this product is recommended
      const reason = this.getRecommendationReason();
      const reasonTag = document.createElement('div');
      reasonTag.classList.add('recommendation-reason');
      reasonTag.textContent = reason;
      
      // Insert after product information
      const cardInfo = card.querySelector('.card__information');
      if (cardInfo) {
        cardInfo.appendChild(reasonTag);
      }
      
      // Add hover effect to show "Quick Add" button
      const quickAddButton = document.createElement('button');
      quickAddButton.classList.add('quick-add__button');
      quickAddButton.setAttribute('type', 'button');
      quickAddButton.innerHTML = '<span>Quick Add</span>';
      
      // Add event listener for quick add
      quickAddButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Get product variant ID
        const productId = card.dataset.productId;
        if (!productId) return;
        
        // Add loading state
        quickAddButton.classList.add('loading');
        quickAddButton.innerHTML = '<span>Adding...</span>';
        
        // Add to cart via AJAX
        this.quickAddToCart(productId, 1)
          .then(response => {
            // Show success state
            quickAddButton.classList.remove('loading');
            quickAddButton.classList.add('success');
            quickAddButton.innerHTML = '<span>Added!</span>';
            
            // Reset after 2 seconds
            setTimeout(() => {
              quickAddButton.classList.remove('success');
              quickAddButton.innerHTML = '<span>Quick Add</span>';
            }, 2000);
            
            // Update cart drawer or notification
            this.updateCartUI();
          })
          .catch(error => {
            // Show error state
            quickAddButton.classList.remove('loading');
            quickAddButton.classList.add('error');
            quickAddButton.innerHTML = '<span>Error</span>';
            
            // Reset after 2 seconds
            setTimeout(() => {
              quickAddButton.classList.remove('error');
              quickAddButton.innerHTML = '<span>Quick Add</span>';
            }, 2000);
            
            console.error('Error adding to cart:', error);
          });
      });
      
      // Insert quick add button
      const cardInner = card.querySelector('.card__inner');
      if (cardInner) {
        const quickAddContainer = document.createElement('div');
        quickAddContainer.classList.add('card__quick-add');
        quickAddContainer.appendChild(quickAddButton);
        cardInner.appendChild(quickAddContainer);
      }
    });
  }
  
  getRecommendationReason() {
    // Return a random personalized reason why this product is recommended
    const reasons = [
      'Perfect match',
      'Completes your collection',
      'Trending now',
      'Customers also bought',
      'Goes well together',
      'Editor\'s pick',
      'Pairs beautifully',
      'Bestseller'
    ];
    
    return reasons[Math.floor(Math.random() * reasons.length)];
  }
  
  quickAddToCart(variantId, quantity = 1) {
    // Add item to cart via AJAX
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => response.json());
  }
  
  updateCartUI() {
    // Update cart count and possibly show mini-cart
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count
        const cartCountElements = document.querySelectorAll('.cart-count-bubble');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
          element.classList.toggle('hide', cart.item_count === 0);
        });
        
        // Show mini cart if it exists
        const miniCart = document.querySelector('.mini-cart');
        if (miniCart && typeof miniCart.show === 'function') {
          miniCart.show();
        }
      })
      .catch(error => console.error('Error updating cart UI:', error));
  }
}